import { FC, useEffect } from "react";

import { Stack } from "@mui/material";
import { getCountry, OrganizationCountry, selectableCountryRegionData } from "@simplyk/common";
import { Country, Region } from "country-region-data";
import { UseFormReturn, useWatch } from "react-hook-form";

import { NewsletterFormSignupData } from "../types";
import { newsletterSelectIconSx, newsletterSelectPlaceholderSx } from "../helpers/fieldStyles";

import { isRequired } from "@/common/helpers/validators";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { useDefaultCountryRegion } from "@/common/hooks/useDefaultCountryRegion";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const getCountryLabel = (country: Country) => `${country.countryName} (${country.countryShortCode})`;
const getRegionLabel = (region: Region) => `${region.name} (${region.shortCode})`;

const SCROLLABLE_MENU_PROPS = { PaperProps: { sx: { maxHeight: 400 } } };

type NewsletterFormAddressFieldsProps = {
  form: UseFormReturn<NewsletterFormSignupData>;
  organization: { country: OrganizationCountry; region?: string | null };
  previewConfiguration?: {
    isPreview: boolean;
  };
};

export const NewsletterFormAddressFields: FC<NewsletterFormAddressFieldsProps> = ({
  form,
  organization,
  previewConfiguration,
}) => {
  const { t } = useTranslate();
  const { errors } = form.formState;

  const country = useWatch({ control: form.control, name: "country" });
  const { currentCountryRegions } = useCountryRegion(country);
  const { defaultCountry, defaultRegion } = useDefaultCountryRegion(organization);

  useEffect(() => {
    if (defaultCountry && !form.getValues("country") && !previewConfiguration?.isPreview) {
      form.setValue("country", defaultCountry);
    }
  }, [defaultCountry, form, previewConfiguration?.isPreview]);

  useEffect(() => {
    if (country === defaultCountry && defaultRegion && !form.getValues("state") && !previewConfiguration?.isPreview) {
      form.setValue("state", defaultRegion);
    }
  }, [country, defaultCountry, defaultRegion, form, previewConfiguration?.isPreview]);

  return (
    <Stack gap={1}>
      {/* Address Line */}
      <DashboardTextField
        name="address"
        data-test="form-address"
        control={form.control}
        placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.address")}
        rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.address.required"))]}
        errorMessage={errors.address?.message}
        disableAutocomplete
      />

      {/* ZIP Code and City */}
      <Stack direction="row" gap={1}>
        <DashboardTextField
          name="zipCode"
          data-test="form-postal-code"
          control={form.control}
          placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.postalCode")}
          rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.postalCode.required"))]}
          errorMessage={errors.zipCode?.message}
          disableAutocomplete
        />
        <DashboardTextField
          name="city"
          data-test="form-city"
          control={form.control}
          placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.city")}
          rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.city.required"))]}
          errorMessage={errors.city?.message}
          disableAutocomplete
        />
      </Stack>

      {/* Country and State dropdowns */}
      <Stack direction="row" gap={1}>
        <DashboardSelect
          control={form.control}
          name="country"
          rules={[isRequired(t("common", "isRequired"))]}
          data-test="form-country"
          MenuProps={SCROLLABLE_MENU_PROPS}
          renderValue={(value) => {
            if (!value) {
              return (
                <DashboardTypography variant="body2" sx={newsletterSelectPlaceholderSx}>
                  {t("dashboard", "newsletterForms.editor.step.questions.field.country")}
                </DashboardTypography>
              );
            }
            const country = getCountry(value.toString());
            if (country) {
              return getCountryLabel(country);
            }
            return value.toString();
          }}
          required
          defaultValue={previewConfiguration?.isPreview ? undefined : defaultCountry}
          error={Boolean(form.formState.errors.country?.message)}
          helperText={form.formState.errors.country?.message}
          sx={newsletterSelectIconSx}
        >
          {selectableCountryRegionData.map((country) => (
            <DashboardMenuItem key={country.countryName} value={country.countryName} data-test="form-country-option">
              {getCountryLabel(country)}
            </DashboardMenuItem>
          ))}
        </DashboardSelect>
        <DashboardSelect
          control={form.control}
          name="state"
          rules={[isRequired(t("common", "isRequired"))]}
          data-test="form-region"
          MenuProps={SCROLLABLE_MENU_PROPS}
          renderValue={(value) => {
            if (!value) {
              return (
                <DashboardTypography variant="body2" sx={newsletterSelectPlaceholderSx}>
                  {t("dashboard", "newsletterForms.editor.step.questions.field.region")}
                </DashboardTypography>
              );
            }
            const region = currentCountryRegions.find((region) => region.shortCode === value);
            if (region) {
              return getRegionLabel(region);
            }
            return value.toString();
          }}
          required
          defaultValue={previewConfiguration?.isPreview ? undefined : defaultRegion}
          error={Boolean(form.formState.errors.state?.message)}
          helperText={form.formState.errors.state?.message}
          sx={newsletterSelectIconSx}
        >
          {currentCountryRegions.map((region, index) => (
            <DashboardMenuItem key={index} value={region.shortCode} data-test="form-region-option">
              {getRegionLabel(region)}
            </DashboardMenuItem>
          ))}
        </DashboardSelect>
      </Stack>
    </Stack>
  );
};
